import { getTenantById } from '@tenants';
import { useThemeContext, useUserContext } from 'contexts';
import { useMemo } from 'react';
import { CountryCode, CountryCodeList } from 'types/countries';

type ListingRestrictions = {
  readonly canViewSchools: boolean;
  readonly canViewWalkScore: boolean;
  readonly canViewPropertyAppraisal: boolean;
  readonly canViewDemographics: boolean;
};

/**
 * Hook to determine listing restrictions based on tenant feature flags and geographic context.
 *
 * This hook is primarily used to control access to certain features (like school information)
 * based on tenant configuration and geographic context.
 *
 * @param listingCountryCode indicates the country code of the listing. Default is `CountryCodeList.CANADA`.
 *
 * @returns An object containing restriction flags:
 *   - `canViewSchools`: Boolean indicating whether school information can be displayed
 *   - `canViewWalkScore`: Boolean indicating whether walk score can be displayed
 *   - `canViewPropertyAppraisal`: Boolean indicating whether property appraisal can be displayed
 *
 * @example
 * ```tsx
 * const { canViewSchools } = useListingRestrictions(CountryCodeList.CANADA);
 *
 * if (canViewSchools) {
 *   // Show school-related components
 * }
 * ```
 *
 * @restrictions
 * School information visibility is controlled by the tenant's `AddressPage.showSchools` feature flag:
 * - If `showSchools` is a boolean: directly controls visibility
 * - If `showSchools` is a function: calls the function with country code, site location, and user location
 * - If `showSchools` is undefined: schools are hidden by default
 *
 * Walk score visibility is controlled by the tenant's `AddressPage.showWalkScore` feature flag:
 * - If `showWalkScore` is a boolean: directly controls visibility
 * - If `showWalkScore` is a function: calls the function with country code, site location, and user location
 * - If `showWalkScore` is undefined: walk score is visible by default
 *
 * Property appraisal visibility is controlled by the tenant's `AddressPage.showPropertyAppraisal` feature flag:
 * - If `showPropertyAppraisal` is a boolean: directly controls visibility
 * - If `showPropertyAppraisal` is a function: calls the function with country code, site location, and user location
 * - If `showPropertyAppraisal` is undefined: property appraisal is visible by default
 *
 * Demographics visibility is controlled by the tenant's `AddressPage.showDemographics` feature flag:
 * - If `showDemographics` is a boolean: directly controls visibility
 * - If `showDemographics` is a function: calls the function with country code, site location, and user location
 * - If `showDemographics` is undefined: demographics are hidden by default
 *
 * @note
 * This hook uses `useMemo` for performance optimization, recalculating only when
 * dependencies change (isCanadianListing, siteLocation, userLocation, tenantId).
 */
export const useListingRestrictions = (listingCountryCode: CountryCode = CountryCodeList.CANADA): ListingRestrictions => {
  const { userLocation, siteLocation } = useUserContext();
  const { tenantId } = useThemeContext();

  const restrictions = useMemo(() => {
    const tenant = getTenantById(tenantId);
    const restrictions = {
      canViewSchools: false,
      canViewWalkScore: true,
      canViewPropertyAppraisal: true,
      canViewDemographics: false,
    };

    const { showSchools, showWalkScore, showPropertyAppraisal, showDemographics } = tenant?.features?.AddressPage || {};

    if (showSchools !== undefined && showSchools !== null) {
      if (typeof showSchools === 'function') {
        restrictions.canViewSchools = showSchools(listingCountryCode, siteLocation, userLocation?.countryCode);
      } else {
        restrictions.canViewSchools = showSchools;
      }
    }

    if (showWalkScore !== undefined && showWalkScore !== null) {
      if (typeof showWalkScore === 'function') {
        restrictions.canViewWalkScore = showWalkScore(listingCountryCode, siteLocation, userLocation?.countryCode);
      } else {
        restrictions.canViewWalkScore = showWalkScore;
      }
    }

    if (showPropertyAppraisal !== undefined && showPropertyAppraisal !== null) {
      if (typeof showPropertyAppraisal === 'function') {
        restrictions.canViewPropertyAppraisal = showPropertyAppraisal(listingCountryCode, siteLocation, userLocation?.countryCode);
      } else {
        restrictions.canViewPropertyAppraisal = showPropertyAppraisal;
      }
    }

    if (showDemographics !== undefined && showDemographics !== null) {
      if (typeof showDemographics === 'function') {
        restrictions.canViewDemographics = showDemographics(listingCountryCode, siteLocation, userLocation?.countryCode);
      } else {
        restrictions.canViewDemographics = showDemographics;
      }
    }

    return restrictions;
  }, [listingCountryCode, siteLocation, userLocation, tenantId]);

  return restrictions;
};